import React, { useState } from 'react';
import './support.css';

// Demo orders data with complete tracking info
const demoOrders = [
  {
    id: 'ord_002',
    orderNumber: '#HOM-2026-001',
    brand: 'Nike',
    brandLogo: '✓',
    brandColor: '#000000',
    orderDate: '15 Jan 2026',
    estimatedDelivery: '22 Jan 2026',
    status: 'ready',
    statusText: 'Ready',
    statusBadgeColor: '#4CAF50',
    items: [
      'Running Shoes',
      'Sports Water Bottle'
    ],
    arrivedAt: '19 Jan 2026',
    source: 'Nike Store',
    itemCount: 2,
    courier: 'DHL Express',
    trackingCode: 'DHL-IT-9876543210',
    courierLogo: '📦',
    co2Saved: '1.8 kg',
    warehouse: 'Milan Central Hub'
  },
  {
    id: 'ord_003',
    orderNumber: '#HOM-2026-003',
    brand: 'On Running',
    brandLogo: 'On',
    brandColor: '#000000',
    orderDate: '16 Jan 2026',
    estimatedDelivery: '23 Jan 2026',
    status: 'ready',
    statusText: 'Ready',
    statusBadgeColor: '#4CAF50',
    items: [
      'Cloudmonster Shoes',
      'Running Socks'
    ],
    arrivedAt: '19 Jan 2026',
    source: 'On Running Store',
    itemCount: 2,
    courier: 'FedEx Express',
    trackingCode: 'FDX-IT-1234567890',
    courierLogo: '📦',
    co2Saved: '1.5 kg',
    warehouse: 'Milan Central Hub'
  },
  {
    id: 'ord_001',
    orderNumber: '#HOM-2026-002',
    brand: 'ZARA',
    brandLogo: 'ZARA',
    brandColor: '#999',
    orderDate: '18 Jan 2026',
    estimatedDelivery: '25 Jan 2026',
    status: 'pending',
    statusText: 'Arriving in 2 days',
    statusSubtext: 'In Transit',
    statusBadgeColor: '#999',
    items: [
      'Wireless Headphones',
      'Phone Case',
      'USB-C Cable'
    ],
    estimatedWarehouse: '21 Jan 2026',
    source: 'Amazon',
    itemCount: 3,
    courier: 'UPS Standard',
    trackingCode: 'UPS-1Z-X987654321',
    courierLogo: '🚚',
    co2Saved: '2.1 kg',
    warehouse: 'Rome Distribution Center'
  }
];

function HomeScreen({ userId, country, onLogout }) {
  const [orders] = useState(demoOrders);
  const [showEmpty] = useState(false); // Toggle this to show empty state

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [viewMode, setViewMode] = useState('home'); // 'home', 'tracking', 'timeline', 'support', 'finding-driver', 'driver-found', 'group-delivery'
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  const [groupedPackages, setGroupedPackages] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'ready', 'pending', 'delivered'
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleAmazonConnect = () => {
    alert('Amazon account connection flow will be implemented here.\n\nThis will:\n1. Redirect to Amazon OAuth\n2. Request order history permission\n3. Sync your orders automatically\n4. Enable pickup from Amazon Lockers');
  };

  const handleToggleSelection = (orderId, event) => {
    event.stopPropagation();
    
    const selectedOrder = orders.find(o => o.id === orderId);
    
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        // Unselecting
        return prev.filter(id => id !== orderId);
      } else {
        // Selecting - check warehouse compatibility
        if (prev.length > 0) {
          const firstSelected = orders.find(o => o.id === prev[0]);
          if (firstSelected.warehouse !== selectedOrder.warehouse) {
            alert(`Cannot group packages from different warehouses!\n\nSelected: ${firstSelected.warehouse}\nThis package: ${selectedOrder.warehouse}\n\nOnly packages in the same warehouse can be grouped.`);
            return prev;
          }
        }
        return [...prev, orderId];
      }
    });
  };

  const handleGroupDelivery = () => {
    if (selectedOrders.length < 2) {
      alert('Please select at least 2 packages to group delivery');
      return;
    }
    
    const selectedOrdersData = orders.filter(o => selectedOrders.includes(o.id));
    
    // Check if all orders are in the same warehouse
    const warehouses = [...new Set(selectedOrdersData.map(o => o.warehouse))];
    if (warehouses.length > 1) {
      alert(`Cannot group delivery!\n\nPackages are in different warehouses:\n${warehouses.join('\n')}\n\nOnly packages in the same warehouse can be grouped.`);
      return;
    }
    
    // Generate individual PIN for each package
    const packagesWithPins = selectedOrdersData.map(order => ({
      ...order,
      securityPin: Math.floor(1000 + Math.random() * 9000).toString()
    }));
    
    setGroupedPackages(packagesWithPins);
    
    // Show finding driver animation first
    setViewMode('finding-group-driver');
    
    // Simulate finding driver (3 seconds)
    setTimeout(() => {
      setViewMode('group-delivery');
    }, 3000);
  };

  const handleDeliver = () => {
    const readyOrders = orders.filter(o => o.status === 'ready');
    if (readyOrders.length === 0) {
      return;
    }
    
    // If no selection, use first ready order
    const orderToDeliver = selectedOrders.length === 0 
      ? readyOrders[0] 
      : orders.find(o => selectedOrders.includes(o.id));
    
    setSelectedOrder(orderToDeliver);
    
    // Show transition effect
    setIsTransitioning(true);
    
    // Navigate to finding driver after brief transition
    setTimeout(() => {
      setIsTransitioning(false);
      handleFindDriver();
    }, 500);
  };

  const handleTrackOrder = (order) => {
    setSelectedOrder(order);
    if (order.status === 'ready') {
      setViewMode('tracking'); // Map view for ready orders
    } else {
      setViewMode('timeline'); // Timeline view for pending orders
    }
  };

  const handleBackToHome = () => {
    setViewMode('home');
    setSelectedOrder(null);
  };

  const handleOpenSupport = () => {
    setViewMode('support');
  };

  const handleFindDriver = () => {
    setViewMode('finding-driver');
    
    // Simulate finding driver after 3 seconds
    setTimeout(() => {
      setDriverInfo({
        name: 'Marco R.',
        rating: 4.9,
        vehicle: 'Vespa',
        estimatedTime: '30 minutes',
        deliveryAddress: selectedOrder?.items?.[0] || 'Your location',
        co2Saved: '2.3 kg' // Mock CO2 savings
      });
      setViewMode('driver-found');
    }, 3000);
  };

  // Render support view
  if (viewMode === 'support') {
    return (
      <div className="home-container">
        <header className="header-tracking">
          <button className="back-btn-header" onClick={handleBackToHome}>←</button>
          <h1 className="header-title-small">Support</h1>
        </header>

        <main className="main-content">
          <div className="support-container">
            <div className="support-header">
              <h2>How can we help you?</h2>
              <p>We're here to assist with your deliveries</p>
            </div>

            <div className="support-section">
              <h3>📞 Contact Us</h3>
              <div className="support-option">
                <strong>Phone Support</strong>
                <p>+39 02 1234 5678</p>
                <p className="support-hours">Mon-Fri: 9:00 - 18:00</p>
              </div>
              <div className="support-option">
                <strong>Email Support</strong>
                <p>support@noni3.com</p>
                <p className="support-hours">Response within 24 hours</p>
              </div>
              <div className="support-option">
                <strong>WhatsApp</strong>
                <p>+39 345 678 9012</p>
                <p className="support-hours">Available 24/7</p>
              </div>
            </div>

            <div className="support-section">
              <h3>❓ Frequently Asked Questions</h3>
              <div className="faq-item">
                <strong>How long does delivery take?</strong>
                <p>Once your package arrives at our warehouse, you can request delivery within 30-45 minutes.</p>
              </div>
              <div className="faq-item">
                <strong>Can I track my order?</strong>
                <p>Yes! Track all your orders in real-time from your home screen.</p>
              </div>
              <div className="faq-item">
                <strong>What if I'm not home?</strong>
                <p>You can reschedule delivery for when you're available through the app.</p>
              </div>
            </div>

            <div className="support-section">
              <h3>📧 Send us a message</h3>
              <form className="support-form">
                <input type="text" placeholder="Your name" className="support-input" />
                <input type="email" placeholder="Your email" className="support-input" />
                <textarea placeholder="How can we help?" className="support-textarea" rows="4"></textarea>
                <button type="submit" className="deliver-button" style={{ position: 'static', transform: 'none' }}>
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Render finding driver for group delivery
  if (viewMode === 'finding-group-driver' && groupedPackages.length >= 2) {
    return (
      <div className="home-container">
        <header className="app-header">
          <div className="header-content">
            <h1 className="header-title">Homie</h1>
          </div>
        </header>

        <main className="main-content">
          <div className="finding-driver-container">
            <div className="spinner"></div>
            <h2 className="finding-title">Finding a Driver</h2>
            <p className="finding-subtitle">Looking for the best driver for your {groupedPackages.length} packages...</p>
            <div className="finding-details">
              <p className="finding-detail">📦 Grouping {groupedPackages.length} packages</p>
              <p className="finding-detail">📍 {groupedPackages[0].warehouse}</p>
              <p className="finding-detail">🌱 Saving {groupedPackages.reduce((sum, pkg) => {
                return sum + parseFloat(pkg.co2Saved.replace(' kg', ''));
              }, 0).toFixed(1)} kg CO₂</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Render group delivery view
  if (viewMode === 'group-delivery' && groupedPackages.length >= 2) {
    const totalCO2 = groupedPackages.reduce((sum, pkg) => {
      return sum + parseFloat(pkg.co2Saved.replace(' kg', ''));
    }, 0);

    const groupDriverInfo = {
      name: 'Marco Rossi',
      rating: '4.9',
      vehicle: 'Electric Van',
      estimatedTime: '30 min',
      co2Saved: `${totalCO2.toFixed(1)} kg`
    };

    return (
      <div className="home-container">
        <header className="app-header">
          <div className="header-content">
            <button className="back-button-header" onClick={handleBackToHome}>
              ← Back
            </button>
            <h1 className="header-title">Group Delivery</h1>
          </div>
        </header>

        <main className="main-content">
          <div className="group-delivery-container">
            {/* Group Delivery Header */}
            <div className="group-delivery-header">
              <div className="group-header-icon">📦</div>
              <div className="group-header-content">
                <h2 className="group-title">Grouped Delivery Request</h2>
                <p className="group-subtitle">{groupedPackages.length} packages • One driver</p>
                <p className="group-warehouse">📍 {groupedPackages[0].warehouse}</p>
              </div>
            </div>

            {/* CO2 Savings Banner */}
            <div className="group-co2-banner">
              <div className="group-co2-icon">🌱</div>
              <div className="group-co2-content">
                <p className="group-co2-label">Total CO₂ Saved</p>
                <p className="group-co2-value">{totalCO2.toFixed(1)} kg</p>
                <p className="group-co2-desc">By grouping your deliveries</p>
              </div>
            </div>

            {/* Packages in Group */}
            <div className="grouped-packages-section">
              <h3 className="section-title">Packages in this delivery</h3>
              {groupedPackages.map((pkg, index) => (
                <div key={pkg.id} className="grouped-package-card">
                  <div className="grouped-package-header">
                    <div className="package-number-badge">{index + 1}</div>
                    <div className="grouped-package-info">
                      <div className="grouped-brand-row">
                        <div 
                          className="brand-logo-circle-small" 
                          style={{ 
                            background: '#000',
                            color: '#fff'
                          }}
                        >
                          {pkg.brandLogo}
                        </div>
                        <div>
                          <h4 className="grouped-brand-name">{pkg.brand}</h4>
                          <p className="grouped-order-number">{pkg.orderNumber}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Individual PIN for this package */}
                  <div className="package-pin-card">
                    <div className="pin-icon-small">🔒</div>
                    <div className="pin-info">
                      <span className="pin-label-small">Security PIN</span>
                      <span className="pin-code-small">{pkg.securityPin}</span>
                    </div>
                  </div>

                  <div className="grouped-package-details">
                    <span className="detail-mini">📦 {pkg.courier}</span>
                    <span className="detail-mini">🌱 {pkg.co2Saved} saved</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Driver Assignment */}
            <div className="group-driver-section">
              <h3 className="section-title">Your Driver</h3>
              <div className="group-driver-card">
                <div className="group-driver-avatar">
                  <span>MR</span>
                </div>
                <div className="group-driver-info">
                  <h4 className="driver-name">{groupDriverInfo.name}</h4>
                  <div className="driver-meta-row">
                    <span className="rating-badge">★ {groupDriverInfo.rating}</span>
                    <span className="vehicle-badge">⚡ {groupDriverInfo.vehicle}</span>
                  </div>
                  <p className="driver-eta">Arriving in {groupDriverInfo.estimatedTime}</p>
                </div>
                <button className="call-driver-btn-compact" onClick={() => alert('Calling driver...')}>
                  📞
                </button>
              </div>
            </div>

            {/* Delivery Progress */}
            <div className="group-delivery-progress">
              <h3 className="section-title">Delivery Status</h3>
              <div className="progress-timeline">
                <div className="progress-step-group active">
                  <div className="step-dot completed"></div>
                  <div className="step-text">
                    <p className="step-label-bold">Driver Assigned</p>
                    <p className="step-time">Just now</p>
                  </div>
                </div>
                
                <div className="progress-line active"></div>
                
                <div className="progress-step-group active">
                  <div className="step-dot pending pulse"></div>
                  <div className="step-text">
                    <p className="step-label-bold">Collecting Packages</p>
                    <p className="step-time">In progress...</p>
                  </div>
                </div>
                
                <div className="progress-line"></div>
                
                <div className="progress-step-group">
                  <div className="step-dot"></div>
                  <div className="step-text">
                    <p className="step-label-bold">Delivering to You</p>
                    <p className="step-time">Est. {groupDriverInfo.estimatedTime}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Notes */}
            <div className="group-delivery-note">
              <div className="note-icon">ℹ️</div>
              <div className="note-text">
                <strong>Verification Required</strong>
                <p>Please provide the individual PIN code for each package to the driver upon delivery.</p>
              </div>
            </div>

            <div className="group-delivery-note" style={{ background: '#f0f9ff', borderColor: '#0284c7' }}>
              <div className="note-icon">📍</div>
              <div className="note-text">
                <strong>Same Warehouse Delivery</strong>
                <p>These packages are grouped because they're at {groupedPackages[0].warehouse}. Only packages in the same warehouse can be grouped for delivery.</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="group-action-buttons">
              <button className="track-live-button" onClick={() => alert('Live tracking coming soon!')}>
                📍 Track Live
              </button>
              <button className="cancel-group-button" onClick={handleBackToHome}>
                Cancel Delivery
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Render timeline view for pending orders
  if (viewMode === 'timeline' && selectedOrder) {
    const timelineSteps = [
      { label: 'Order Placed', date: '09 Aug 2025, 10:00am', completed: true },
      { label: 'Order Confirmed', date: '09 Aug 2025, 10:30am', completed: true },
      { label: 'Packed the product', date: '09 Aug 2025, 12:00pm', completed: true },
      { label: 'Arrived in the warehouse', date: '10 Aug 2025, 02:00pm', completed: true },
      { label: 'Near by Courier facility', date: '10 Aug 2025, 03:00pm', completed: true },
      { label: 'Out for Delivery', date: '12 Aug 2025, 05:00pm', completed: false, current: true },
      { label: 'Delivered', date: '12 Aug 2025, 09:00pm', completed: false }
    ];

    return (
      <div className="home-container">
        <header className="header-tracking">
          <button className="back-btn-header" onClick={handleBackToHome}>←</button>
          <h1 className="header-title-small">Order Status</h1>
        </header>

        <main className="main-content">
          <div className="order-detail-header">
            <div className="brand-logo-large" style={{ color: selectedOrder.brandColor }}>
              {selectedOrder.brandLogo}
            </div>
            <div className="order-detail-info">
              <h2 className="order-detail-brand">{selectedOrder.brand}</h2>
              <p className="order-detail-number">{selectedOrder.orderNumber}</p>
              <p className="order-detail-status">{selectedOrder.statusText}</p>
            </div>
          </div>

          <div className="order-info-card">
            <div className="info-row">
              <span className="info-label">Order Date:</span>
              <span className="info-value">{selectedOrder.orderDate}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Est. Delivery:</span>
              <span className="info-value">{selectedOrder.estimatedDelivery}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Courier:</span>
              <span className="info-value">{selectedOrder.courierLogo} {selectedOrder.courier}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Tracking:</span>
              <span className="info-value tracking-code">{selectedOrder.trackingCode}</span>
            </div>
          </div>

          <div className="timeline-container">
            {timelineSteps.map((step, index) => (
              <div key={index} className="timeline-step">
                <div className="timeline-marker">
                  <div className={`timeline-dot ${step.completed ? 'completed' : ''} ${step.current ? 'current' : ''}`}>
                    {step.completed && '✓'}
                  </div>
                  {index < timelineSteps.length - 1 && (
                    <div className={`timeline-line ${step.completed ? 'completed' : ''}`}></div>
                  )}
                </div>
                <div className="timeline-content">
                  <h4 className="timeline-label">{step.label}</h4>
                  <p className="timeline-date">{step.date}</p>
                </div>
              </div>
            ))}
          </div>

          <button className="deliver-button" onClick={handleBackToHome}>
            Back to Orders
          </button>
        </main>
      </div>
    );
  }

  // Render finding driver animation
  if (viewMode === 'finding-driver') {
    return (
      <div className="home-container">
        <header className="header-tracking">
          <button className="back-btn-header" onClick={handleBackToHome}>←</button>
          <h1 className="header-title-small">{selectedOrder?.orderNumber}</h1>
        </header>

        <main className="main-content-tracking">
          <div className="finding-driver-container">
            <div className="driver-loader">
              <div className="spinner"></div>
            </div>
            <h2 className="finding-driver-title">Finding a driver...</h2>
            <p className="finding-driver-subtitle">This will only take a moment</p>
          </div>
        </main>
      </div>
    );
  }

  // Render driver found screen
  if (viewMode === 'driver-found' && driverInfo) {
    const securityPin = '4739';
    
    return (
      <div className="home-container">
        <header className="header-tracking">
          <button className="back-btn-header" onClick={handleBackToHome}>←</button>
          <h1 className="header-title-small">{selectedOrder?.orderNumber}</h1>
        </header>

        <main className="main-content-tracking">
          <div className="driver-found-container">
            {/* Map Section */}
            <div className="driver-map-container">
              <div className="map-view">
                <div className="map-marker warehouse">📍</div>
                <div className="map-marker destination">🏠</div>
                <div className="map-route"></div>
              </div>
              <div className="map-overlay-info">
                <div className="delivery-eta-compact">
                  <span className="eta-icon">🕒</span>
                  <span className="eta-text">{driverInfo.estimatedTime}</span>
                </div>
              </div>
            </div>

            {/* Security PIN */}
            <div className="security-pin-card">
              <div className="pin-header">
                <span className="pin-icon">🔒</span>
                <span className="pin-label">Security PIN</span>
              </div>
              <div className="pin-code">{securityPin}</div>
              <p className="pin-description">Share this code with your driver for verification</p>
            </div>
            
            {/* Driver Info */}
            <div className="driver-info-card-sober">
              <div className="driver-info-header">
                <div className="driver-avatar-sober">
                  <span>MR</span>
                </div>
                <div className="driver-details-sober">
                  <h3>{driverInfo.name}</h3>
                  <div className="driver-meta">
                    <span className="rating-badge">★ {driverInfo.rating}</span>
                    <span className="vehicle-type">{driverInfo.vehicle}</span>
                  </div>
                </div>
              </div>
              <button className="call-driver-btn" onClick={() => alert('Calling driver...')}>
                <span>📞</span> Call Driver
              </button>
            </div>

            {/* Delivery Status */}
            <div className="delivery-progress-card">
              <h3 className="progress-title">Delivery Status</h3>
              <div className="progress-steps">
                <div className="progress-step active">
                  <div className="step-indicator completed"></div>
                  <div className="step-content">
                    <p className="step-label">Picking up package</p>
                    <p className="step-location">Warehouse</p>
                  </div>
                </div>
                
                <div className="progress-connector active"></div>
                
                <div className="progress-step">
                  <div className="step-indicator pending"></div>
                  <div className="step-content">
                    <p className="step-label">Delivering to you</p>
                    <p className="step-location">Est. {driverInfo.estimatedTime}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CO2 Savings Card */}
            <div className="co2-savings-card">
              <div className="co2-icon">🌱</div>
              <div className="co2-content">
                <p className="co2-label">CO₂ Emissions Saved</p>
                <p className="co2-value">{driverInfo.co2Saved}</p>
                <p className="co2-description">By grouping deliveries</p>
              </div>
            </div>

            <button className="track-button-sober" onClick={handleBackToHome}>
              View Live Tracking
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Render map tracking view for ready orders
  if (viewMode === 'tracking' && selectedOrder) {
    const trackingHistory = [
      { label: 'Order Placed', date: selectedOrder.orderDate, time: '10:00 AM', completed: true },
      { label: 'Order Confirmed', date: selectedOrder.orderDate, time: '10:30 AM', completed: true },
      { label: 'Shipped', date: selectedOrder.orderDate, time: '2:00 PM', completed: true },
      { label: 'In Transit', date: '18 Jan 2026', time: '8:00 AM', completed: true },
      { label: 'Arrived at Warehouse', date: selectedOrder.arrivedAt, time: '3:00 PM', completed: true, current: true },
      { label: 'Out for Delivery', date: 'Pending', time: '', completed: false },
      { label: 'Delivered', date: 'Pending', time: '', completed: false }
    ];

    return (
      <div className="home-container">
        <header className="header-tracking">
          <button className="back-btn-header" onClick={handleBackToHome}>←</button>
          <h1 className="header-title-small">{selectedOrder.orderNumber}</h1>
          <button className="help-button">Help</button>
        </header>

        <main className="main-content-tracking">
          {/* Order Brand Header */}
          <div className="order-detail-brand-header">
            <div 
              className="brand-logo-detail" 
              style={{ 
                background: selectedOrder.status === 'ready' ? '#000' : '#f0f0f0',
                color: selectedOrder.status === 'ready' ? '#fff' : '#999'
              }}
            >
              {selectedOrder.brandLogo}
            </div>
            <div className="brand-info-detail">
              <h2 className="brand-name-detail">{selectedOrder.brand}</h2>
              <p className="order-number-detail">{selectedOrder.orderNumber}</p>
            </div>
            <div className={`status-badge-detail ${selectedOrder.status === 'ready' ? 'ready' : 'pending'}`}>
              {selectedOrder.statusText}
            </div>
          </div>

          <div className="tracking-message">
            <p>Your delivery is ready at our warehouse</p>
            <p className="tracking-time">Available for 30-45 min delivery window</p>
          </div>

          {/* Tracking History Timeline */}
          <div className="tracking-history-card">
            <h3 className="tracking-history-title">Tracking History</h3>
            <div className="tracking-history-list">
              {trackingHistory.map((step, index) => (
                <div key={index} className="tracking-history-step">
                  <div className="tracking-history-marker">
                    <div className={`tracking-history-dot ${step.completed ? 'completed' : ''} ${step.current ? 'current' : ''}`}>
                      {step.completed && '✓'}
                    </div>
                    {index < trackingHistory.length - 1 && (
                      <div className={`tracking-history-line ${step.completed ? 'completed' : ''}`}></div>
                    )}
                  </div>
                  <div className="tracking-history-content">
                    <h4 className="tracking-history-label">{step.label}</h4>
                    <p className="tracking-history-date">
                      {step.date} {step.time && `• ${step.time}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="map-placeholder">
            <div className="map-content">
              <div className="map-pin">📍</div>
              <p style={{ marginTop: '120px', color: '#666' }}>Warehouse Location</p>
              <p style={{ fontSize: '14px', color: '#999' }}>Your package is ready here</p>
            </div>
          </div>

          <div className="tracking-actions">
            <button className="action-btn-outline" onClick={() => alert(`Calling warehouse...\n\nPhone: +39 02 1234 5678`)}>
              📞 Call Driver
            </button>
            <button className="action-btn-outline" onClick={() => setViewMode('timeline')}>
              📋 Order Details
            </button>
          </div>

          <button className="deliver-button" onClick={handleFindDriver}>
            Find Driver
          </button>
        </main>
      </div>
    );
  }

  // Home view with orders list
  return (
    <div className="home-container">
      <header className="header">
        <div className="header-content">
          <div>
            <h1 className="header-title">Homie</h1>
            <p className="header-subtitle">Your Orders</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="icon-button" onClick={handleOpenSupport} title="Support">
              ❓
            </button>
            <button className="logout-button" onClick={onLogout}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className={`main-content ${isTransitioning ? 'transitioning' : ''}`}>
        {/* Transition Overlay */}
        {isTransitioning && (
          <div className="transition-overlay">
            <div className="transition-content">
              <div className="loader-spinner"></div>
              <p className="transition-text">Finding your driver...</p>
            </div>
          </div>
        )}

        {/* Amazon Connect Banner - Elegant */}
        <div className="amazon-connect-elegant">
          <div className="amazon-elegant-left">
            <div className="amazon-icon-elegant">🛒</div>
            <div className="amazon-text-elegant">
              <p className="amazon-title-elegant">Amazon</p>
              <p className="amazon-subtitle-elegant">Auto-sync orders & pickup from lockers</p>
            </div>
          </div>
          <button className="amazon-btn-elegant" onClick={handleAmazonConnect}>
            <span className="connect-icon">⚡</span>
            Connect
          </button>
        </div>

        {/* Status Filter Pills */}
        <div className="status-filter-pills">
          <button 
            className={`filter-pill ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
          <button 
            className={`filter-pill ${statusFilter === 'ready' ? 'active' : ''}`}
            onClick={() => setStatusFilter('ready')}
          >
            Ready
          </button>
          <button 
            className={`filter-pill ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            Pending
          </button>
          <button 
            className={`filter-pill ${statusFilter === 'delivered' ? 'active' : ''}`}
            onClick={() => setStatusFilter('delivered')}
          >
            Delivered
          </button>
        </div>

        {/* CO2 Impact Summary - Discreet */}
        {!showEmpty && (
          <div className="co2-impact-discreet">
            <span className="co2-icon-small">🌱</span>
            <span className="co2-text-small">
              {orders.reduce((sum, order) => {
                const kg = parseFloat(order.co2Saved);
                return sum + kg;
              }, 0).toFixed(1)} kg CO₂ saved
            </span>
          </div>
        )}

        {/* Orders Section */}
        <div className="orders-section">
          {showEmpty ? (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <h3>No orders yet</h3>
              <p>Your orders will appear here once you make a purchase</p>
            </div>
          ) : (
            <>
              <div className="orders-list">
                {orders
                  .filter(order => {
                    if (statusFilter === 'all') return true;
                    if (statusFilter === 'ready') return order.status === 'ready';
                    if (statusFilter === 'pending') return order.status === 'pending';
                    if (statusFilter === 'delivered') return order.status === 'delivered';
                    return true;
                  })
                  .map(order => (
                  <div 
                    key={order.id} 
                    className={`order-card-modern ${selectedOrders.includes(order.id) ? 'selected' : ''}`}
                    onClick={() => handleTrackOrder(order)}
                  >
                    <div className="order-card-full">
                      <div className="order-card-header-row">
                        {order.status === 'ready' && (
                          <input
                            type="checkbox"
                            className="order-checkbox"
                            checked={selectedOrders.includes(order.id)}
                            onChange={(e) => handleToggleSelection(order.id, e)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <div className="order-card-left">
                          <div 
                            className="brand-logo-circle" 
                            style={{ 
                              background: order.status === 'ready' ? '#000' : '#f0f0f0',
                              color: order.status === 'ready' ? '#fff' : '#999'
                            }}
                          >
                            {order.brandLogo}
                          </div>
                        </div>
                        
                        <div className="order-card-center">
                          <h3 className="order-brand-name">{order.brand}</h3>
                          <p className="order-number-small">{order.orderNumber}</p>
                        </div>

                        <div className="order-card-right">
                          {order.status === 'ready' ? (
                            <div className="status-badge-ready">
                              <span className="status-check">✓</span>
                              <span className="status-text">Ready</span>
                            </div>
                          ) : (
                            <div className="status-badge-pending">
                              <span className="status-days">{order.statusText}</span>
                              <span className="status-subtext">{order.statusSubtext}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="order-card-details">
                        <div className="order-detail-row">
                          <span className="detail-label">📅 Ordered:</span>
                          <span className="detail-value">{order.orderDate}</span>
                        </div>
                        <div className="order-detail-row">
                          <span className="detail-label">📦 Est. Delivery:</span>
                          <span className="detail-value">{order.estimatedDelivery}</span>
                        </div>
                        <div className="order-detail-row">
                          <span className="detail-label">{order.courierLogo} Courier:</span>
                          <span className="detail-value">{order.courier}</span>
                        </div>
                        <div className="order-detail-row co2-row">
                          <span className="detail-label">🌱 CO₂ Saved:</span>
                          <span className="detail-value co2-highlight">{order.co2Saved}</span>
                        </div>
                        {order.status === 'ready' && (
                          <div className="order-detail-row warehouse-row">
                            <span className="detail-label">📍 Warehouse:</span>
                            <span className="detail-value warehouse-text">{order.warehouse}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {orders.some(o => o.status === 'ready') && (
                <>
                  {selectedOrders.length >= 2 ? (
                    <div className="grouped-delivery-section">
                      <div className="group-delivery-info">
                        <span className="group-icon">📦</span>
                        <div className="group-text">
                          <p className="group-count">{selectedOrders.length} packages selected</p>
                          <p className="group-warehouse-mini">📍 {orders.find(o => o.id === selectedOrders[0]).warehouse}</p>
                          <p className="group-benefit">
                            Save {orders.filter(o => selectedOrders.includes(o.id)).reduce((sum, order) => {
                              return sum + parseFloat(order.co2Saved.replace(' kg', ''));
                            }, 0).toFixed(1)} kg CO₂
                          </p>
                        </div>
                      </div>
                      <button className="group-delivery-button" onClick={handleGroupDelivery}>
                        Group Delivery
                      </button>
                    </div>
                  ) : (
                    <button className="deliver-button-cta" onClick={handleDeliver}>
                      <span className="cta-icon">🚴</span>
                      Request Delivery Now
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default HomeScreen;
